<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { h } from 'vue';
import { useVueTable, getCoreRowModel } from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import SmmProgress from './SmmProgress.vue';
import Button from 'dashboard/components-next/button/Button.vue';
const props = defineProps({
  rows: { type: Array, default: () => [] },
  disabled: Boolean,
});
const emit = defineEmits(['edit']);
const statuses = {
  Processing: 'В работе',
  Completed: 'Завершён',
  Canceled: 'Отменён',
  Partial: 'Частично',
  Problem: 'Проблема',
};
const reviews = {
  missing_order_number: 'Нет номера заказа',
  awaiting_api: 'Ожидается API',
  missing_group: 'Выберите группу',
  missing_date: 'Укажите дату',
  refund_reconciliation: 'Сверьте стоимость и возврат',
  not_completed: 'Ожидается завершение',
  manual_expense_mismatch: 'Ручной расход не совпадает',
  possible_manual_duplicate: 'Возможен дубль ручного расхода',
};
const money = value =>
  value == null
    ? 'Не получена'
    : `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 6 })} ₽`;
const label = row =>
  row.provider_order_id
    ? `Заказ №${row.provider_order_id}`
    : `Без номера · запись ${row.id}`;
const state = row =>
  [
    row.error_code ? 'Ошибка API' : null,
    reviews[row.review_code] ||
      (row.effective_charge !== null ? 'Учтён' : 'На сверке'),
  ]
    .filter(Boolean)
    .join(' · ');
const columns = [
  {
    id: 'number',
    header: 'Заказ / дата',
    cell: ({ row }) =>
      h(Button, {
        label: `${label(row.original)} · ${row.original.ordered_on || 'дата неизвестна'}`,
        slate: true,
        ghost: true,
        disabled: props.disabled,
        class:
          'w-full min-w-0 text-left [&>span]:whitespace-normal [&>span]:break-words',
        onClick: () => emit('edit', row.original),
      }),
  },
  {
    id: 'performance',
    header: 'Количество по сервису / цена подписчика',
    size: 340,
    cell: ({ row }) => h(SmmProgress, { metrics: row.original }),
  },
  { accessorKey: 'group_title', header: 'Группа', size: 260 },
  {
    id: 'status',
    header: 'Статус / учёт',
    cell: ({ row }) =>
      `${statuses[row.original.provider_status] || 'Не проверен'} · ${state(row.original)}`,
  },
  {
    id: 'cost',
    header: 'Стоимость сервиса / в учёте',
    cell: ({ row }) =>
      `${money(row.original.charge)} / ${money(row.original.effective_charge)}`,
  },
];
const table = useVueTable({
  get data() {
    return props.rows;
  },
  columns,
  getCoreRowModel: getCoreRowModel(),
  enableSorting: false,
});
</script>

<template>
  <div class="hidden lg:block overflow-x-auto">
    <Table :table="table" fixed type="compact" class="w-full min-w-[1100px]" />
  </div>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:hidden gap-3">
    <article
      v-for="row in rows"
      :key="row.id"
      class="border border-n-weak rounded-xl p-4 min-w-0 flex flex-col gap-2"
    >
      <Button
        :label="label(row)"
        slate
        ghost
        :disabled="disabled"
        class="w-full justify-start min-w-0 [&>span]:whitespace-normal [&>span]:break-words"
        @click="emit('edit', row)"
      />
      <p class="text-sm text-n-slate-12 m-0 break-words">
        {{ row.group_title || 'Группа не указана' }} ·
        {{ row.ordered_on || 'Дата неизвестна' }}
      </p>
      <p class="text-sm text-n-slate-11 m-0">
        {{ statuses[row.provider_status] || 'Не проверен' }} · {{ state(row) }}
      </p>
      <SmmProgress :metrics="row" />
      <p class="text-sm text-n-slate-12 m-0">
        Сервис: {{ money(row.charge) }} · учтено:
        {{ money(row.effective_charge) }}
      </p>
    </article>
  </div>
</template>
